import {
  useEffect,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  measureIframeContentSize,
  resolveNextContentSize,
  suppressResizeObserverLoopError,
  type IIframeContentSize,
} from './iframeAutoSize';

const MUTATION_DEBOUNCE_MS = 120;
const RETRY_DELAYS_MS = [200, 800, 1600, 2400, 3200];

interface IIframeAutoSizeRuntime {
  destroyed: boolean;
  hasLoaded: boolean;
  rafId: number;
  debounceTimer: number;
  observer: MutationObserver | null;
  retryTimers: number[];
}

type TSetContentSize = Dispatch<SetStateAction<IIframeContentSize | undefined>>;

const createAutoSizeRuntime = (): IIframeAutoSizeRuntime => ({
  destroyed: false,
  hasLoaded: false,
  rafId: 0,
  debounceTimer: 0,
  observer: null,
  retryTimers: [],
});

const bindErrorSuppress = (target: Window | null | undefined): void => {
  target?.addEventListener('error', suppressResizeObserverLoopError, true);
};

const unbindErrorSuppress = (target: Window | null | undefined): void => {
  target?.removeEventListener('error', suppressResizeObserverLoopError, true);
};

const updateIframeSize = (
  iframe: HTMLIFrameElement,
  runtime: IIframeAutoSizeRuntime,
  setContentSize: TSetContentSize,
): void => {
  if (runtime.destroyed) {
    return;
  }
  try {
    const doc = iframe.contentDocument;
    if (!doc) {
      return;
    }
    const nextSize = measureIframeContentSize(doc);
    if (!nextSize) {
      return;
    }
    setContentSize((current: IIframeContentSize | undefined) => (
      resolveNextContentSize(current, nextSize)
    ));
  } catch (_) {
    // 跨域 iframe 无法访问 contentDocument，保持原有高度行为。
  }
};

const scheduleIframeSizeUpdate = (
  iframe: HTMLIFrameElement,
  runtime: IIframeAutoSizeRuntime,
  setContentSize: TSetContentSize,
  delay: number,
): void => {
  if (runtime.destroyed) {
    return;
  }
  if (runtime.debounceTimer) {
    window.clearTimeout(runtime.debounceTimer);
  }
  runtime.debounceTimer = window.setTimeout(() => {
    runtime.debounceTimer = 0;
    if (runtime.rafId) {
      return;
    }
    runtime.rafId = window.requestAnimationFrame(() => {
      runtime.rafId = 0;
      updateIframeSize(iframe, runtime, setContentSize);
    });
  }, delay);
};

const observeIframeDocument = (
  iframe: HTMLIFrameElement,
  runtime: IIframeAutoSizeRuntime,
  setContentSize: TSetContentSize,
  doc: Document,
): void => {
  runtime.observer?.disconnect();
  runtime.observer = null;
  if (typeof MutationObserver === 'undefined' || !doc.body) {
    return;
  }
  runtime.observer = new MutationObserver(() => {
    scheduleIframeSizeUpdate(
      iframe,
      runtime,
      setContentSize,
      MUTATION_DEBOUNCE_MS,
    );
  });
  runtime.observer.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
  });
};

const handleIframeLoad = (
  iframe: HTMLIFrameElement,
  runtime: IIframeAutoSizeRuntime,
  setContentSize: TSetContentSize,
): void => {
  if (runtime.destroyed || runtime.hasLoaded) {
    return;
  }
  try {
    const doc = iframe.contentDocument;
    if (!doc) {
      return;
    }
    // readyState=complete 与 load 可能连续触发，先占位避免重入。
    runtime.hasLoaded = true;
    bindErrorSuppress(iframe.contentWindow);
    observeIframeDocument(iframe, runtime, setContentSize, doc);
    updateIframeSize(iframe, runtime, setContentSize);
    // iframe load 只代表子文档就绪，同源 React 页的接口数据/图表会更晚才撑开高度。
    // MutationObserver 可能漏掉纯绘制变化，因此在 load 后再补测几次。
    RETRY_DELAYS_MS.forEach((delay: number) => {
      runtime.retryTimers.push(
        window.setTimeout(() => {
          scheduleIframeSizeUpdate(iframe, runtime, setContentSize, 0);
        }, delay),
      );
    });
  } catch (_) {
    runtime.hasLoaded = false;
    // 跨域 iframe 无法访问 contentDocument，保持原有高度行为。
  }
};

const cleanupIframeAutoSize = (
  iframe: HTMLIFrameElement,
  runtime: IIframeAutoSizeRuntime,
  handleLoad: () => void,
): void => {
  runtime.destroyed = true;
  iframe.removeEventListener('load', handleLoad);
  runtime.observer?.disconnect();
  runtime.retryTimers.forEach((timer: number) => window.clearTimeout(timer));
  if (runtime.debounceTimer) {
    window.clearTimeout(runtime.debounceTimer);
  }
  if (runtime.rafId) {
    window.cancelAnimationFrame(runtime.rafId);
  }
  try {
    unbindErrorSuppress(iframe.contentWindow);
  } catch (_) {
    // 跨域 iframe 无法移除 contentWindow error 监听。
  }
};

const bindIframeAutoSize = (
  iframe: HTMLIFrameElement,
  setContentSize: TSetContentSize,
): (() => void) => {
  const runtime = createAutoSizeRuntime();
  const handleLoad = (): void => {
    handleIframeLoad(iframe, runtime, setContentSize);
  };

  iframe.addEventListener('load', handleLoad);
  try {
    if (iframe.contentDocument?.readyState === 'complete') {
      handleLoad();
    }
  } catch (_) {
    // 跨域 iframe 无法访问 contentDocument，等待 load 后安全降级。
  }

  return (): void => {
    cleanupIframeAutoSize(iframe, runtime, handleLoad);
  };
};

/**
 * 同源 iframe 按内容自适应宽高。
 * 仅在 enabled 时绑定测高与 ResizeObserver loop 错误拦截。
 */
export const useIframeAutoSize = (
  iframeRef: RefObject<HTMLIFrameElement>,
  enabled: boolean,
  resetKey?: string,
): IIframeContentSize | undefined => {
  const [contentSize, setContentSize] = useState<IIframeContentSize | undefined>();

  useEffect(() => {
    if (!enabled) {
      setContentSize(undefined);
      return undefined;
    }
    setContentSize(undefined);
    const iframe = iframeRef.current;
    if (!iframe) {
      return undefined;
    }
    return bindIframeAutoSize(iframe, setContentSize);
  }, [enabled, iframeRef, resetKey]);

  return enabled ? contentSize : undefined;
};
